import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  Volume2,
  Search,
  Play,
  Square,
  Check,
  ChevronsUpDown,
  Star,
  RefreshCw,
  X,
} from 'lucide-react'
import {
  Button,
  Badge,
  Label,
} from '@/components/ui'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { voicesApi, type Voice } from '@/api/endpoints/voices'
import { cn } from '@/lib/utils'

interface VoiceSelectorProps {
  value: string | undefined
  onChange: (voiceId: string) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  showPreview?: boolean
  className?: string
}

// Sample text for voice preview
const PREVIEW_TEXT = "Hello! Thank you for calling Riviera Finance. How can I help you today?"

export function VoiceSelector({
  value,
  onChange,
  label,
  placeholder = "Select a voice...",
  disabled = false,
  showPreview = true,
  className,
}: VoiceSelectorProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Fetch all voices
  const { data, isLoading } = useQuery({
    queryKey: ['voices'],
    queryFn: () => voicesApi.getAll(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const voices = data?.data?.voices || []
  const defaultVoiceId = data?.data?.default_voice_id

  // Voice preview mutation
  const previewMutation = useMutation({
    mutationFn: ({ voiceId, text }: { voiceId: string; text: string }) =>
      voicesApi.preview(voiceId, text),
    onSuccess: (response, variables) => {
      const audioData = response.data.audio_data
      const contentType = response.data.content_type || 'audio/mpeg'
      if (audioData && audioRef.current) {
        // Create data URL from base64 audio
        audioRef.current.src = `data:${contentType};base64,${audioData}`
        audioRef.current.play()
        setPlayingVoiceId(variables.voiceId)
      }
    },
    onError: () => {
      setPlayingVoiceId(null)
    },
  })

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
      }
    }
  }, [])

  // Handle audio ended
  const handleAudioEnded = () => {
    setPlayingVoiceId(null)
  }

  // Stop playing
  const stopPlaying = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setPlayingVoiceId(null)
  }

  // Play voice preview
  const playPreview = (voiceId: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    e?.preventDefault()

    if (playingVoiceId === voiceId) {
      stopPlaying()
      return
    }
    stopPlaying()
    previewMutation.mutate({ voiceId, text: PREVIEW_TEXT })
  }

  // Filter voices based on search
  const filteredVoices = useMemo(() => {
    if (!searchQuery) return voices

    const query = searchQuery.toLowerCase()
    return voices.filter((v: Voice) =>
      v.name?.toLowerCase().includes(query) ||
      v.description?.toLowerCase().includes(query) ||
      v.tags?.some((t: string) => t?.toLowerCase().includes(query))
    )
  }, [voices, searchQuery])

  // Find selected voice
  const selectedVoice = useMemo(() => {
    return voices.find((v: Voice) => v.id === value)
  }, [voices, value])

  // Infer gender from voice metadata
  const inferGender = (voice: Voice): string | null => {
    const text = `${voice.name || ''} ${voice.description || ''} ${voice.tags?.join(' ') || ''}`.toLowerCase()
    if (text.includes('female')) return 'Female'
    if (text.includes('male')) return 'Male'
    return null
  }

  // Get display name for voice
  const getVoiceDisplayName = (voice: Voice) => {
    const gender = inferGender(voice)
    return gender ? `${voice.name} (${gender})` : voice.name
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        style={{ display: 'none' }}
      />

      {label && <Label>{label}</Label>}

      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="flex-1 justify-between"
              disabled={disabled || isLoading}
            >
              {isLoading ? (
                <span className="text-muted-foreground">Loading voices...</span>
              ) : selectedVoice ? (
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                  <span>{getVoiceDisplayName(selectedVoice)}</span>
                  {selectedVoice.id === defaultVoiceId && (
                    <Badge variant="secondary" className="text-xs py-0 px-1">
                      <Star className="h-3 w-3 mr-0.5 fill-yellow-400 text-yellow-400" />
                      Default
                    </Badge>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground">{placeholder}</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <div className="flex flex-col">
              {/* Search Input */}
              <div className="flex items-center border-b px-3">
                <Search className="h-4 w-4 text-muted-foreground mr-2" />
                <input
                  placeholder="Search voices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                  autoFocus
                />
                {searchQuery && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setSearchQuery('')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>

              {/* Voice List */}
              <div className="max-h-[300px] overflow-y-auto p-1">
                {filteredVoices.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No voices found.
                  </div>
                ) : (
                  filteredVoices.map((voice: Voice) => {
                    const isSelected = voice.id === value
                    const isDefault = voice.id === defaultVoiceId
                    const isPlaying = playingVoiceId === voice.id
                    const gender = inferGender(voice)

                    return (
                      <div
                        key={voice.id}
                        onClick={() => {
                          onChange(voice.id)
                          setOpen(false)
                          setSearchQuery('')
                        }}
                        className={cn(
                          'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                          isSelected && 'bg-accent'
                        )}
                      >
                        <div className={cn(
                          'flex h-4 w-4 items-center justify-center rounded-sm border mr-2',
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-muted opacity-50'
                        )}>
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate">{voice.name}</span>
                            {gender && (
                              <span className="text-xs text-muted-foreground">({gender})</span>
                            )}
                            {isDefault && (
                              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            )}
                          </div>
                          {voice.description && (
                            <p className="text-xs text-muted-foreground truncate">
                              {voice.description}
                            </p>
                          )}
                          {voice.tags && voice.tags.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {voice.tags.slice(0, 3).map((tag: string, i: number) => (
                                <Badge key={i} variant="secondary" className="text-xs py-0 px-1">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        {showPreview && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={(e) => playPreview(voice.id, e)}
                            disabled={previewMutation.isPending && !isPlaying}
                          >
                            {isPlaying ? (
                              <Square className="h-3 w-3" />
                            ) : previewMutation.isPending ? (
                              <RefreshCw className="h-3 w-3 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                          </Button>
                        )}
                      </div>
                    )
                  })
                )}
              </div>

              {/* Footer */}
              <div className="border-t p-2 text-xs text-muted-foreground text-center">
                {filteredVoices.length} of {voices.length} voices
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Preview button for selected voice */}
        {showPreview && selectedVoice && (
          <Button
            variant="outline"
            size="icon"
            onClick={() => playPreview(selectedVoice.id)}
            disabled={previewMutation.isPending}
            title="Preview voice"
          >
            {playingVoiceId === selectedVoice.id ? (
              <Square className="h-4 w-4" />
            ) : previewMutation.isPending ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

export default VoiceSelector
